import {
  Action, Form, FormHandlers, Portal, useModal, useNotifications,
} from '@doveit/bricks';
import React from 'react';
import SimpleModal from '../../../components/simple-modal/SimpleModal';
import { Property } from '../../../providers/api/dtos';
import { updateProperty } from '../../../providers/api/property/propertyProvider';
import { Status } from '../../../domain/types';
import { INPUT_PLACEHOLDERS } from '../../../constants';
import { pricePriceValidationSchema } from './EditPropertyPriceAction.schema';

interface ChildFnProps {
  edit: VoidFunction,
  isSaving: boolean,
}

export interface EditPropertyPriceActionProps extends React.AriaAttributes {
  property: Property,
  canEditPrice?: boolean,
  onSuccess?: (updatedProperty: Property) => void,
  children?: (childFnProps: ChildFnProps) => React.ReactNode,
}

export const EDIT_PROPERTY_PRICE_SUCCESS_MESSAGE = "Il prezzo dell'immobile è stato modificato con successo";
export const EDIT_PROPERTY_PRICE_ERROR_MESSAGE = "Non è stato possibile modificare il prezzo dell'immobile";

const EditPropertyPriceAction: React.FC<EditPropertyPriceActionProps> = ({
  property,
  canEditPrice = false,
  onSuccess,
  children,
}) => {
  const modal = useModal();
  const { addSuccess, addError } = useNotifications();
  const [isSaving, setIsSaving] = React.useState(false);

  const formRef = React.useRef<FormHandlers>(null) as React.MutableRefObject<FormHandlers>;

  const submitForm = React.useCallback(() => {
    formRef.current?.handleSubmit();
  }, []);

  const onSubmit = React.useCallback(async (formValues: { price: string }) => {
    try {
      setIsSaving(true);

      const propertyToUpdate: Property = {
        ...property,
        price: formValues.price ? parseInt(formValues.price, 10) : undefined,
      };

      const updatedProperty = await updateProperty(propertyToUpdate.id!, propertyToUpdate);

      setIsSaving(false);
      addSuccess(EDIT_PROPERTY_PRICE_SUCCESS_MESSAGE);
      modal.close();

      onSuccess?.(updatedProperty);
    } catch (error) {
      setIsSaving(false);
      addError(EDIT_PROPERTY_PRICE_ERROR_MESSAGE);
    }
  }, [addError, addSuccess, modal, onSuccess, property]);

  if (![Status.LIVE, Status.ANTEPRIMA].includes(property.status) || !canEditPrice) {
    return null;
  }

  return (
    <>
      {children
        ? children({
          edit: modal.open,
          isSaving,
        })
        : (
          <Action
            aria-label="Modifica il prezzo dell'immobile"
            label="Modifica prezzo"
            size="S"
            onClick={() => modal.open()}
            loading={isSaving}
          />
        )}
      <Portal>
        <SimpleModal
          {...modal}
          title="Modifica prezzo"
          aria-label="Modale per modificare il prezzo dell'immobile"
          footer={(
            <Action
              label="Modifica"
              color="primary"
              emphasis="high"
              aria-label="Pulsante per modificare il prezzo dell'immobile"
              onClick={submitForm}
            />
          )}
        >
          <Form
            loading={isSaving}
            initialValues={{
              price: property.price?.toString() || '',
            }}
            innerRef={formRef}
            onSubmit={onSubmit}
            validationSchema={pricePriceValidationSchema}
          >
            <Form.Item>
              <Form.Input
                name="price"
                label="Prezzo"
                type="number"
                min="0"
                placeholder={INPUT_PLACEHOLDERS.PROPERTY_PRICE}
                aria-label="Campo per inserire il prezzo dell'immobile"
              />
            </Form.Item>
          </Form>
        </SimpleModal>
      </Portal>
    </>
  );
};

export default EditPropertyPriceAction;
